import React, { useState, useRef, createElement } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, RadiologyRequest } from '../../types';
import { Scan, CheckCircle, Upload, X } from 'lucide-react';
// Biometry rows for the obstetric structured form
const BIOMETRY_ROWS = ['GS', 'CRL', 'BPD', 'HC', 'AC', 'FL', 'EFW'];
type ObstetricForm = {
  numberOfFetuses: string;
  presentation: string;
  afiDvp: string;
  fhr: string;
  fetalAnatomy: string;
  fetalMovements: string;
  cervixInternalOs: string;
  placentaA: string;
  placentaB: string;
  fetalA: {
    [key: string]: {
      size: string;
      ega: string;
    };
  };
  fetalB: {
    [key: string]: {
      size: string;
      ega: string;
    };
  };
  impression: string;
  sonographerName: string;
};
type PelvicForm = {
  findings: string;
  sonographerName: string;
};
const blankBiometry = () => BIOMETRY_ROWS.reduce((acc, row) => {
  acc[row] = {
    size: '',
    ega: ''
  };
  return acc;
}, {} as {
  [key: string]: {
    size: string;
    ega: string;
  };
});
export const RadiologyDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser
  } = useData();
  const [selectedRequest, setSelectedRequest] = useState<{
    enc: Encounter;
    rad: RadiologyRequest;
  } | null>(null);
  const [report, setReport] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [obstetricForm, setObstetricForm] = useState<ObstetricForm>({
    numberOfFetuses: '',
    presentation: '',
    afiDvp: '',
    fhr: '',
    fetalAnatomy: '',
    fetalMovements: '',
    cervixInternalOs: '',
    placentaA: '',
    placentaB: '',
    fetalA: blankBiometry(),
    fetalB: blankBiometry(),
    impression: '',
    sonographerName: ''
  });
  const [pelvicForm, setPelvicForm] = useState<PelvicForm>({
    findings: '',
    sonographerName: ''
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Find encounters with pending radiology requests
  const pendingRequests = encounters.flatMap(enc => enc.radiologyRequests.filter(rad => rad.status === 'Pending').map(rad => ({
    enc,
    rad
  })));
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectRequest = (item: {
    enc: Encounter;
    rad: RadiologyRequest;
  }) => {
    setSelectedRequest(item);
    setReport('');
    setImages([]);
    setObstetricForm({
      numberOfFetuses: '',
      presentation: '',
      afiDvp: '',
      fhr: '',
      fetalAnatomy: '',
      fetalMovements: '',
      cervixInternalOs: '',
      placentaA: '',
      placentaB: '',
      fetalA: blankBiometry(),
      fetalB: blankBiometry(),
      impression: '',
      sonographerName: currentUser?.fullName || ''
    });
    setPelvicForm({
      findings: '',
      sonographerName: currentUser?.fullName || ''
    });
  };
  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = event => {
        const img = new Image();
        img.onload = () => {
          // Downscale so the long edge is at most 1280px — keeps each image well under
          // ~150-250KB so multiple scans fit inside Firestore's 1MB document cap.
          const MAX_DIM = 1280;
          let {
            width,
            height
          } = img;
          if (width > MAX_DIM || height > MAX_DIM) {
            if (width > height) {
              height = Math.round(height * MAX_DIM / width);
              width = MAX_DIM;
            } else {
              width = Math.round(width * MAX_DIM / height);
              height = MAX_DIM;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas not supported'));
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.onerror = () => reject(new Error('Could not load image'));
        img.src = event.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Could not read file'));
      reader.readAsDataURL(file);
    });
  };
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (const file of Array.from(files)) {
      if (file.size > 50 * 1024 * 1024) {
        alert(`${file.name} exceeds 50MB limit`);
        continue;
      }
      try {
        const compressed = await compressImage(file);
        setImages(prev => [...prev, compressed]);
      } catch (err) {
        console.error('Failed to compress image:', err);
        alert(`Failed to process ${file.name}`);
      }
    }
    // Reset the input so the same file can be re-selected if needed
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };
  const buildObstetricReport = (f: ObstetricForm): string => {
    const lines: string[] = [];
    lines.push('OBSTETRIC ULTRASOUND REPORT');
    lines.push('');
    lines.push('FINDINGS');
    lines.push(`Number of Fetuses/GS: ${f.numberOfFetuses}`);
    lines.push(`Presentation: ${f.presentation}`);
    lines.push(`AFI/DVP: ${f.afiDvp}`);
    lines.push(`FHR: ${f.fhr}`);
    lines.push(`Fetal Anatomy: ${f.fetalAnatomy}`);
    lines.push(`Fetal Movements: ${f.fetalMovements}`);
    lines.push(`Cervix/Internal OS: ${f.cervixInternalOs}`);
    lines.push('');
    lines.push('BIOMETRY');
    lines.push(`Placenta (Fetal A): ${f.placentaA}`);
    lines.push(`Placenta (Fetal B): ${f.placentaB}`);
    lines.push('');
    lines.push('FETAL A:');
    BIOMETRY_ROWS.forEach(row => {
      lines.push(`  ${row}: Size=${f.fetalA[row].size || '-'}, EGA=${f.fetalA[row].ega || '-'}`);
    });
    lines.push('');
    lines.push('FETAL B:');
    BIOMETRY_ROWS.forEach(row => {
      lines.push(`  ${row}: Size=${f.fetalB[row].size || '-'}, EGA=${f.fetalB[row].ega || '-'}`);
    });
    lines.push('');
    lines.push(`IMPRESSION: ${f.impression}`);
    lines.push('');
    lines.push(`SONOGRAPHER'S NAME: ${f.sonographerName}`);
    return lines.join('\n');
  };
  const buildPelvicReport = (f: PelvicForm): string => {
    return ['ULTRASOUND REPORT', '', 'FINDINGS:', f.findings, '', `SONOGRAPHER'S NAME: ${f.sonographerName}`].join('\n');
  };
  const handleSaveResults = () => {
    if (!selectedRequest || !currentUser) return;
    const scanType = selectedRequest.rad.scanType;
    const isObstetric = scanType === 'OBS Scan' || scanType === 'Anomaly Scan';
    const isPelvic = scanType === 'Pelvic Scan' || scanType === 'Abdominal Scan' || scanType === 'Abdomen/Pelvis Scan';
    let finalReport = report;
    if (isObstetric) {
      finalReport = buildObstetricReport(obstetricForm);
    } else if (isPelvic) {
      finalReport = buildPelvicReport(pelvicForm);
    }
    if (!finalReport.trim() && images.length === 0) {
      alert('Please complete the report or upload images before sending.');
      return;
    }
    const {
      enc,
      rad
    } = selectedRequest;
    const updatedRad: RadiologyRequest = {
      ...rad,
      status: 'Completed',
      report: finalReport,
      images,
      completedAt: new Date().toISOString(),
      completedBy: currentUser.id,
      radiographerName: currentUser.fullName
    };
    const updatedRadRequests = enc.radiologyRequests.map(r => r.id === rad.id ? updatedRad : r);
    // Guard against Firestore's 1MB document cap. If the payload (mostly base64
    // images) is too large, the write would fail silently and the report would
    // never reach the clinician.
    const payloadBytes = JSON.stringify(updatedRadRequests).length;
    if (payloadBytes > 900000) {
      alert(`The combined report + images are too large to send (${Math.round(payloadBytes / 1024)}KB). Remove one or more images and try again.`);
      return;
    }
    // Check if all rads are completed
    const allRadsCompleted = updatedRadRequests.every(r => r.status === 'Completed');
    const updateData: Partial<Encounter> = {
      radiologyRequests: updatedRadRequests,
      pendingRadiology: !allRadsCompleted
    };
    // If all rads done and no pending lab, move back to clinician
    if (allRadsCompleted && !enc.pendingLab) {
      updateData.stage = 'CLINICIAN';
    }
    updateEncounter(enc.id, updateData);
    alert('Scan results saved and sent to Clinician.');
    setSelectedRequest(null);
  };
  if (selectedRequest) {
    const patient = getPatient(selectedRequest.enc.patientId);
    if (!patient) return <div data-id="element-5760" data-mp="3l852xpuygsdf">Patient not found</div>;
    const scanType = selectedRequest.rad.scanType;
    const isObstetric = scanType === 'OBS Scan' || scanType === 'Anomaly Scan';
    const isPelvic = scanType === 'Pelvic Scan' || scanType === 'Abdominal Scan' || scanType === 'Abdomen/Pelvis Scan';
    const isSendDisabled = !isObstetric && !isPelvic ? !report && images.length === 0 : false;
    return <div className="space-y-6" data-id="element-5761" data-mp="32jhzfbsfgb1y">
        <div className="flex justify-between items-center" data-id="element-5762" data-mp="2xkdreoh8ojd7">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-5763" data-mp="2xlylkczn60aj">
            Radiology Results Entry
          </h1>
          <Button variant="outline" onClick={() => setSelectedRequest(null)} data-id="element-5764" data-mp="3cb4ks0yhbqif">
            Back to Queue
          </Button>
        </div>

        <Card className="bg-purple-50 border-purple-200" data-id="element-5765" data-mp="2js7ah069i8cp">
          <div className="flex justify-between items-start" data-id="element-5766" data-mp="2jqmfj9g1cneb">
            <div data-id="element-5767" data-mp="2esl1j2xgve9p">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-5768" data-mp="2et4fy9nwrkiz">
                {patient.firstName} {patient.lastName}
              </h2>
              <p className="text-sm text-gray-600" data-id="element-5769" data-mp="2hdo0kkntqhdt">
                BMC: {patient.bmcFileNumber} • {patient.age} yrs • {patient.sex}
              </p>
            </div>
            <div className="text-right" data-id="element-5770" data-mp="2ccqa7ksnuh18">
              <Badge variant="purple" className="text-sm" data-id="element-5771" data-mp="27fqbxw21yiko">
                {selectedRequest.rad.scanType}
              </Badge>
              <p className="text-xs text-gray-500 mt-1" data-id="element-5772" data-mp="22hf2hmfo6a2z">
                Requested:{' '}
                {new Date(selectedRequest.rad.requestedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        <div className={isObstetric || isPelvic ? 'space-y-6' : 'grid grid-cols-1 lg:grid-cols-2 gap-6'} data-id="element-5773" data-mp="1toblx59q9h44">
          <Card title="Upload Images" data-id="element-5774" data-mp="1jokpuz5iqu1x">
            <div className="space-y-4" data-id="element-5775" data-mp="1jrqg4hpvw1z8">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:bg-gray-50 cursor-pointer" onClick={() => fileInputRef.current?.click()} data-id="element-5776" data-mp="1eru887e2fqee">
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-2" data-id="element-5777" data-mp="19ke15wotlq5e" />
                <p className="text-sm text-gray-600" data-id="element-5778" data-mp="1em3drgroybvl">
                  Click to upload images (PNG/JPG)
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-5779" data-mp="1jjmsehsawqo2">
                  Max size: 50MB per image
                </p>
                <input type="file" ref={fileInputRef} className="hidden" accept="image/png, image/jpeg" multiple onChange={handleImageUpload} data-id="element-5780" data-mp="lujobf4i41l5" />
              </div>

              {images.length > 0 && <div className="grid grid-cols-3 gap-4 mt-4" data-id="element-5781" data-mp="365172xb17pm">
                  {images.map((img, idx) => <div key={idx} className="relative group" data-id="element-5782" data-mp="846h5ezfoqwv" data-mpi={idx}>
                      <img src={img} alt={`Scan ${idx}`} className="h-24 w-full object-cover rounded border border-gray-200" data-id="element-5783" data-mp="82n0hn9v8duk" />
                      <button onClick={() => removeImage(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-5784" data-mp="9ttpbjt68who">
                        <X className="w-4 h-4" data-id="element-5785" data-mp="3dcwjitl3ug4v" />
                      </button>
                    </div>)}
                </div>}
            </div>
          </Card>

          {/* Conditional report area */}
          {isObstetric ? <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8" data-id="element-5786" data-mp="3i93q8olbrvnv">
              {/* Form Header */}
              <div className="text-center border-b-2 border-black pb-4 mb-6" data-id="element-5787" data-mp="12gxqtet22sop">
                <h2 className="font-bold text-xl uppercase tracking-wide text-gray-900" data-id="element-5788" data-mp="xiw66pfzelb4">
                  Obstetric Ultrasound Report
                </h2>
              </div>

              {/* FINDINGS */}
              <div className="mb-8" data-id="element-5789" data-mp="1a3uu48ax5ft0">
                <h3 className="font-bold text-lg mb-4 uppercase border-b border-gray-300 pb-2" data-id="element-5790" data-mp="w2ie5wvrde74">
                  Findings
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4" data-id="element-5791" data-mp="10558q5jtdd80">
                  {[{
                key: 'numberOfFetuses',
                label: 'Number of Fetuses / GS'
              }, {
                key: 'presentation',
                label: 'Presentation'
              }, {
                key: 'afiDvp',
                label: 'AFI / DVP'
              }, {
                key: 'fhr',
                label: 'FHR'
              }, {
                key: 'fetalAnatomy',
                label: 'Fetal Anatomy'
              }, {
                key: 'fetalMovements',
                label: 'Fetal Movements'
              }, {
                key: 'cervixInternalOs',
                label: 'Cervix / Internal OS'
              }].map(field => <div key={field.key} data-id="element-5792" data-mp="16lyndo6xakcz">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide" data-id="element-5793" data-mp="16kx9zd2fpls3">
                        {field.label}
                      </label>
                      <input type="text" value={(obstetricForm as any)[field.key]} onChange={e => setObstetricForm({
                  ...obstetricForm,
                  [field.key]: e.target.value
                })} placeholder={`Enter ${field.label.toLowerCase()}`} className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5794" data-mp="wuklxavevah7" />
                    </div>)}
                </div>
              </div>

              {/* BIOMETRY — redesigned with explicit text boxes per value */}
              <div className="mb-8" data-id="element-5795" data-mp="3m6as6uhyazaw">
                <h3 className="font-bold text-lg mb-4 uppercase border-b border-gray-300 pb-2" data-id="element-5796" data-mp="3r5x5t51oyhk8">
                  Biometry
                </h3>

                {/* Placenta inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-5797" data-mp="3vyzr6pwbekpc">
                  <div data-id="element-5798" data-mp="3s1lcewf2ile">
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase" data-id="element-5799" data-mp="3unrca3nsq1i">
                      Placenta (Fetal A)
                    </label>
                    <input type="text" value={obstetricForm.placentaA} onChange={e => setObstetricForm({
                  ...obstetricForm,
                  placentaA: e.target.value
                })} placeholder="Enter placenta details" className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5800" data-mp="drip2qs3ymeq" />
                  </div>
                  <div data-id="element-5801" data-mp="2ol4flj4drgih">
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase" data-id="element-5802" data-mp="32ikor2e29odx">
                      Placenta (Fetal B)
                    </label>
                    <input type="text" value={obstetricForm.placentaB} onChange={e => setObstetricForm({
                  ...obstetricForm,
                  placentaB: e.target.value
                })} placeholder="Enter placenta details" className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5803" data-mp="2xkj8ep80s365" />
                  </div>
                </div>

                {/* Fetal A / Fetal B biometry — each value gets visible text boxes */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4" data-id="element-5804" data-mp="311dxrv14ew2c">
                  {(['fetalA', 'fetalB'] as const).map(side => <div key={side} className="bg-purple-50/40 border border-purple-200 rounded-lg p-3" data-id="element-5805" data-mp="2m5xxv9dv05y1">
                      <p className="font-bold text-center mb-3 bg-purple-100 text-purple-900 py-1.5 rounded" data-id="element-5806" data-mp="kcrwtyywmw88">
                        {side === 'fetalA' ? 'FETAL A' : 'FETAL B'}
                      </p>

                      {/* Column labels */}
                      <div className="grid grid-cols-[3rem_1fr_1fr] gap-2 mb-2 px-1" data-id="element-5807" data-mp="39a8979k13kzg">
                        <span data-id="element-5808" data-mp="349ucgnnvnxgq"></span>
                        <span className="text-xs font-bold text-gray-600 uppercase text-center" data-id="element-5809" data-mp="2zic7zpmnber0">
                          Size
                        </span>
                        <span className="text-xs font-bold text-gray-600 uppercase text-center" data-id="element-5810" data-mp="2uipvjien62j9">
                          EGA
                        </span>
                      </div>

                      <div className="space-y-2" data-id="element-5811" data-mp="2lhru97vl894d">
                        {BIOMETRY_ROWS.map(row => <div key={row} className="grid grid-cols-[3rem_1fr_1fr] gap-2 items-center" data-id="element-5812" data-mp="2gjzzm63k68wh">
                            <label htmlFor={`${side}-${row}-size`} className="text-sm font-bold text-gray-800 text-center" data-id="element-5813" data-mp="eqsc49dskkzi">
                              {row}
                            </label>
                            <input id={`${side}-${row}-size`} type="text" value={obstetricForm[side][row].size} onChange={e => setObstetricForm({
                      ...obstetricForm,
                      [side]: {
                        ...obstetricForm[side],
                        [row]: {
                          ...obstetricForm[side][row],
                          size: e.target.value
                        }
                      }
                    })} placeholder="Size" className="w-full border-2 border-gray-300 rounded-md p-1.5 text-sm bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5814" data-mp="maumemykseys" />
                            <input type="text" value={obstetricForm[side][row].ega} onChange={e => setObstetricForm({
                      ...obstetricForm,
                      [side]: {
                        ...obstetricForm[side],
                        [row]: {
                          ...obstetricForm[side][row],
                          ega: e.target.value
                        }
                      }
                    })} placeholder="EGA" className="w-full border-2 border-gray-300 rounded-md p-1.5 text-sm bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5815" data-mp="341ohdbs6o79j" />
                          </div>)}
                      </div>
                    </div>)}
                </div>
              </div>

              {/* IMPRESSION */}
              <div className="mb-8" data-id="element-5816" data-mp="5quxh3p8wv8q">
                <h3 className="font-bold text-lg mb-4 uppercase border-b border-gray-300 pb-2" data-id="element-5817" data-mp="5ptiwp90tqm9">
                  Impression
                </h3>
                <textarea value={obstetricForm.impression} onChange={e => setObstetricForm({
              ...obstetricForm,
              impression: e.target.value
            })} placeholder="Enter clinical impression..." className="w-full p-3 min-h-[140px] text-sm border-2 border-gray-300 rounded-md focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5818" data-mp="3rxt5fxcsjsp1" />
              </div>

              {/* Sonographer footer */}
              <div className="pt-6 border-t border-gray-300" data-id="element-5819" data-mp="3fha8ku0ghses">
                <div className="max-w-md" data-id="element-5820" data-mp="31ou4rqzqdqct">
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide" data-id="element-5821" data-mp="31q54nfq22dyn">
                    Sonographer's Name
                  </label>
                  <input type="text" value={obstetricForm.sonographerName} onChange={e => setObstetricForm({
                ...obstetricForm,
                sonographerName: e.target.value
              })} placeholder="Enter sonographer name" className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5822" data-mp="3tfi3kwdxmjnb" />
                </div>
              </div>
            </div> : isPelvic ? <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8" data-id="element-5823" data-mp="21gnmgl8wl77o">
              <div className="text-center border-b-2 border-black pb-4 mb-6" data-id="element-5824" data-mp="21iqdn8lsl6dz">
                <h2 className="font-bold text-xl uppercase tracking-wide text-gray-900" data-id="element-5825" data-mp="2ffwswt7b4myg">
                  Ultrasound Report
                </h2>
                <p className="text-sm font-medium text-gray-600 mt-1" data-id="element-5826" data-mp="1mrhn8xa2fgzs">
                  {scanType}
                </p>
              </div>
              <div className="mb-8" data-id="element-5827" data-mp="1htzbkz4nyno3">
                <h3 className="font-bold text-lg mb-4 uppercase border-b border-gray-300 pb-2" data-id="element-5828" data-mp="1hqtp7rnf5zw2">
                  Findings
                </h3>
                <textarea value={pelvicForm.findings} onChange={e => setPelvicForm({
              ...pelvicForm,
              findings: e.target.value
            })} placeholder="Enter detailed findings..." className="w-full p-3 min-h-[300px] text-sm border-2 border-gray-300 rounded-md focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5829" data-mp="1rpszfenu23l6" />
              </div>
              <div className="pt-6 border-t border-gray-300" data-id="element-5830" data-mp="b9dfxu1c3z1h">
                <div className="max-w-md" data-id="element-5831" data-mp="g383j8k28y5u">
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide" data-id="element-5832" data-mp="g416rzjt95j8">
                    Sonographer's Name
                  </label>
                  <input type="text" value={pelvicForm.sonographerName} onChange={e => setPelvicForm({
                ...pelvicForm,
                sonographerName: e.target.value
              })} placeholder="Enter sonographer name" className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5833" data-mp="pyt2syxqvkbw" />
                </div>
              </div>
            </div> : <Card title="Radiologist Report" data-id="element-5834" data-mp="2jil9guyz8ecb">
              <div className="space-y-4 h-full flex flex-col" data-id="element-5835" data-mp="2jjcujbuy6toc">
                <textarea className="w-full flex-1 min-h-[200px] border border-gray-300 rounded-md p-3 focus:ring-purple-500 focus:border-purple-500" placeholder="Enter detailed report findings here..." value={report} onChange={e => setReport(e.target.value)} data-id="element-5836" data-mp="2fiau4rpaobks" />
                <div className="text-sm text-gray-500 text-right" data-id="element-5837" data-mp="3396ptzwvfqxb">
                  Report by: {currentUser?.fullName}
                </div>
              </div>
            </Card>}
        </div>

        <div className="flex justify-end" data-id="element-5838" data-mp="1rpkk4o0yc2d5">
          <Button onClick={handleSaveResults} size="lg" className="bg-purple-600 hover:bg-purple-700 text-white" disabled={isSendDisabled} data-id="element-5839" data-mp="1mskluzacg3wl">
            <CheckCircle className="w-5 h-5 mr-2" data-id="element-5840" data-mp="1t9ccyke6xamh" /> Send to Clinician
          </Button>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-5841" data-mp="1f91n9koma22w">
      <div className="flex justify-between items-center" data-id="element-5842" data-mp="1jawlggb93zcg">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-5843" data-mp="1j9btmygbmuia">
          Radiology Dashboard
        </h1>
        <Badge variant="purple" className="text-sm px-3 py-1" data-id="element-5844" data-mp="15hxbsuy5gzct">
          {pendingRequests.length} Pending Scans
        </Badge>
      </div>

      <Card noPadding data-id="element-5845" data-mp="vlc05qrmkq00">
        <ul className="divide-y divide-gray-200" data-id="element-5846" data-mp="vm3nyfezdjfq">
          {pendingRequests.length > 0 ? pendingRequests.map(item => {
          const patient = getPatient(item.enc.patientId);
          if (!patient) return null;
          return <li key={item.rad.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-5847" data-mp="rjquoqhf7dzu">
                  <div className="flex items-center" data-id="element-5848" data-mp="7ygvcqbtwj5j">
                    <div className="bg-purple-100 p-2 rounded-full mr-4" data-id="element-5849" data-mp="2z413kimo9sx">
                      <Scan className="h-6 w-6 text-purple-600" data-id="element-5850" data-mp="316zosxujb99" />
                    </div>
                    <div data-id="element-5851" data-mp="gwisgusfyrnj">
                      <p className="text-sm font-medium text-blue-600" data-id="element-5852" data-mp="cx9x5lhr6lpv">
                        {patient.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-5853" data-mp="qv8b4ps5wg4u">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <p className="text-sm font-medium text-purple-700" data-id="element-5854" data-mp="10n4a0322oaa4">
                        {item.rad.scanType}
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectRequest(item)} className="bg-purple-600 hover:bg-purple-700 text-white" data-id="element-5855" data-mp="19n13chzepxbi">
                    Upload Scan
                  </Button>
                </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-5856" data-mp="2gz8e51plj3mv">
              <Scan className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-5857" data-mp="2h1uk4wwu9b2z" />
              <p className="text-lg font-medium text-gray-900" data-id="element-5858" data-mp="2l2muxulczo1d">
                No pending scan requests
              </p>
            </li>}
        </ul>
      </Card>
    </div>;
};